import { useEffect, useId, useReducer, useRef, useState } from 'react';
import { trackAnalytics } from './lib/analytics';
import { activationReducer, initialActivationState, isActivationCandidate, ACTIVATION_EXPOSURE_MS } from './lib/activationJourney';
import type { ChineseLocale } from './ChineseLocale';
import './firstValueJourney.css';

type Props = {
  locale: 'en' | ChineseLocale; system: 'ziwei' | 'bazi' | 'tarot';
  headline: string; insight: string; evidence: string[]; action: string;
  example?: boolean; paid?: boolean; resultOrdinal?: number; onExplore: () => void; onEdit?: () => void;
  onOffers?: () => void; samplePath?: string;
};
const copy = {
  en: {
    title: 'Start with one useful insight.', label: 'YOUR FIRST READING · FREE',
    intro: 'You do not need to understand every symbol. Read this one idea, compare it with a real situation, then choose a small next step.',
    basis: '1 · See what this reading is based on', question: '2 · Does this connect to your experience?',
    fits: 'Yes, it connects', partly: 'Partly', not_me: 'Not really',
    negative: 'Keep the mismatch. A symbolic reading can be wrong for your situation; do not force a fit. Check the inputs or explore another theme.',
    next: '3 · A small next step—not a forecast', try_step: 'Use this as my next step', check_first: 'Gather evidence first',
    selected: 'Chosen for this reading. This is a plan, not a claim that you have done it.',
    explore: 'Explore my free result', edit: 'Check my details', offers: 'Compare report contents & prices', sample: 'See a fictional sample ↗',
    optional: 'Your free result stays available. A report adds depth, not certainty. No need to complete these steps to see prices.',
    sampleNote: 'These may still be example inputs. Check your birth record before treating this as your personal reading.',
    sampleFeedback: 'Explore freely; sample results are excluded from personal activation measurement.',
    boundary: 'A reflection prompt, not evidence that an event will happen. Feedback is optional; no personal story is submitted.',
  },
  'zh-Hans': {
    title: '先读懂一条与你有关的线索。', label: '第一条解读 · 免费',
    intro: '不用先学会所有术语。把这条解读与一件真实经历放在一起，再挑一个可以核对的小行动。',
    basis: '1 · 看看这条解读的依据', question: '2 · 与你的实际经历有联系吗？',
    fits: '有联系', partly: '部分有', not_me: '不太符合',
    negative: '保留不符合的地方，不必硬套。象征解读可能不适合你的情况；可以核对输入，或继续看其他主题。',
    next: '3 · 一个小行动，不是预测', try_step: '把它作为我的下一步', check_first: '先收集现实证据',
    selected: '已选作本次解读的行动方向；不代表你已经完成了行动。',
    explore: '继续探索免费结果', edit: '核对我的资料', offers: '比较报告内容与价格', sample: '看看虚构样本（繁体）↗',
    optional: '免费结果一直可用。报告增加解读深度，不增加预测确定性；无需完成这些步骤也能查看价格。',
    sampleNote: '当前可能仍是示例资料。请核对出生记录，再当作自己的解读。',
    sampleFeedback: '可以自由体验；示例结果不计入个人激活指标。',
    boundary: '这是反思线索，不是事件会发生的证据。反馈自愿，不提交你的个人经历。',
  },
  'zh-Hant': {
    title: '先讀懂一條與你有關的線索。', label: '第一條解讀 · 免費',
    intro: '不用先學會所有術語。把這條解讀與一件真實經歷放在一起，再挑一個可以核對的小行動。',
    basis: '1 · 看看這條解讀的依據', question: '2 · 與你的實際經歷有聯繫嗎？',
    fits: '有聯繫', partly: '部分有', not_me: '不太符合',
    negative: '保留不符合的地方，不必硬套。象徵解讀可能不適合你的情況；可以核對輸入，或繼續看其他主題。',
    next: '3 · 一個小行動，不是預測', try_step: '把它作為我的下一步', check_first: '先收集現實證據',
    selected: '已選作本次解讀的行動方向；不代表你已經完成了行動。',
    explore: '繼續探索免費結果', edit: '核對我的資料', offers: '比較報告內容與價格', sample: '看看虛構樣本 ↗',
    optional: '免費結果一直可用。報告增加解讀深度，不增加預測確定性；無需完成這些步驟也能查看價格。',
    sampleNote: '目前可能仍是示例資料。請核對出生記錄，再當作自己的解讀。',
    sampleFeedback: '可以自由體驗；示例結果不計入個人啟用指標。',
    boundary: '這是反思線索，不是事件會發生的證據。回饋自願，不提交你的個人經歷。',
  },
};

export function FirstValueJourney(p: Props) {
  const c = copy[p.locale];
  const id = useId();
  const [state, dispatch] = useReducer(activationReducer, initialActivationState);
  const [cohort] = useState(() => {
    try { return localStorage.getItem('ziheng:guided-value-seen:v1') ? 'returning_guided_result' : 'first_guided_result'; }
    catch { return 'unknown'; }
  });
  const exposureRef = useRef<HTMLParagraphElement>(null);
  const sent = useRef(new Set<string>());
  const emit = (event: string, payload: Record<string, string | number | boolean> = {}, once = false) => {
    if (once && sent.current.has(event)) return;
    sent.current.add(event);
    trackAnalytics(event, { chart_system: p.system, journey_cohort: cohort, is_example: Boolean(p.example), already_paid: Boolean(p.paid), result_ordinal: p.resultOrdinal || 0, activation_version: 1, ...payload });
  };
  useEffect(() => {
    const node = exposureRef.current;
    if (!node || !('IntersectionObserver' in window)) return; // Content stays usable without measurement.
    let visible = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const stop = () => { if (timer) clearTimeout(timer); timer = undefined; };
    const update = () => {
      stop();
      if (visible && document.visibilityState === 'visible' && !sent.current.has('first_insight_engaged')) {
        timer = setTimeout(() => {
          dispatch({ type: 'exposed' });
          emit('first_insight_engaged', { exposure_seconds: ACTIVATION_EXPOSURE_MS / 1000 }, true);
          if (!p.example && !p.paid) {
            try { localStorage.setItem('ziheng:guided-value-seen:v1', '1'); } catch { /* Optional, no raw input persisted. */ }
          }
        }, ACTIVATION_EXPOSURE_MS);
      }
    };
    const observer = new IntersectionObserver(([entry]) => { visible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.5); update(); }, { threshold: 0.5 });
    observer.observe(node);
    document.addEventListener('visibilitychange', update);
    return () => { stop(); observer.disconnect(); document.removeEventListener('visibilitychange', update); };
  }, []);
  useEffect(() => {
    if (!p.paid && isActivationCandidate(state, Boolean(p.example))) emit('activation_completed', {
      resonance: state.resonance!, action_choice: state.action!,
      insight_count: 1, evidence_count: 1, action_count: 1,
    }, true);
  }, [state, p.example, p.paid]);
  return <section className="first-value" aria-labelledby={`${id}-title`}>
    <header><small>{c.label}</small><h3 id={`${id}-title`}>{c.title}</h3><p>{c.intro}</p></header>
    {p.example && <aside className="first-value-example">{c.sampleNote} {c.sampleFeedback}</aside>}
    <h4>{p.headline}</h4><p ref={exposureRef} className="first-value-insight">{p.insight}</p>
    <details onToggle={(e) => { if (e.currentTarget.open) { dispatch({ type: 'evidence' }); emit('insight_evidence_opened', {}, true); } }}>
      <summary>{c.basis}</summary><ul>{p.evidence.map((e, i) => <li key={i}>{e}</li>)}</ul><p>{c.boundary}</p>
    </details>
    <fieldset><legend>{c.question}</legend><div className="first-value-choices">{(['fits', 'partly', 'not_me'] as const).map(value => <button type="button" key={value} aria-pressed={state.resonance === value} onClick={() => { if (state.resonance === value) return; dispatch({ type: 'resonance', value }); emit('insight_resonance_selected', { resonance: value }); }}>{c[value]}</button>)}</div></fieldset>
    {state.resonance === 'not_me' && <p role="status" className="first-value-mismatch">{c.negative}</p>}
    <div className="first-value-action"><h4>{c.next}</h4><p>{p.action}</p><div className="first-value-choices">{(['try_step', 'check_first'] as const).map(value => <button type="button" key={value} aria-pressed={state.action === value} onClick={() => { if (state.action === value) return; dispatch({ type: 'action', value }); emit('reading_action_selected', { action_choice: value }); }}>{c[value]}</button>)}</div>{state.action && <p role="status">{c.selected}</p>}</div>
    <footer><div><button type="button" onClick={() => { emit('activation_nudge_clicked', { nudge_target: 'free_result' }); p.onExplore(); }}>{c.explore} →</button>{p.onEdit && <button type="button" onClick={() => { emit('activation_nudge_clicked', { nudge_target: 'edit' }); p.onEdit?.(); }}>{c.edit}</button>}</div>
      {!p.paid && p.onOffers && <div><button type="button" onClick={() => { emit('activation_nudge_clicked', { nudge_target: 'offers' }); p.onOffers?.(); }}>{c.offers} →</button>{p.samplePath && <a href={p.samplePath} target="_blank" rel="noopener" onClick={() => emit('activation_nudge_clicked', { nudge_target: 'sample' })}>{c.sample}</a>}</div>}
      <small>{p.system === 'tarot' || !p.onOffers || p.paid ? c.boundary : c.optional}</small>
    </footer>
  </section>;
}
